/**
 * Store locator — bridges custom Figma shell with TA MAPPY (BSS) widget output.
 */
(function () {
  const roots = document.querySelectorAll('[data-store-locator]');
  if (!roots.length) return;

  const SELECTORS = {
    page: '#store-locator-page',
    pageParent: '#store-locator-page-parent',
    searchBox: '#bss-map-search-box',
    searchInput: '#bss-search-input',
    searchBtn: '#bss-search-btn',
    searchResult: '#bss-search-result',
    mapWrapper: '#bss-map-wrapper',
  };

  const HIDE_IN_WIDGET = [
    SELECTORS.searchBox,
    '.search-bar-container',
    '.search-bar-above-map',
    '#bss-wrapper-header',
    '.filter-categories',
    '#bss-filter-tag',
    '#bss-filter-country',
    '#bss-radius-filter',
    '#bss-radius-filter-cover',
    '#bss-wrapper-tag',
    '#bss-wrapper-country',
    '#bss-wrapper-radius',
    '#map-button',
    '.store-list-button-icon',
    '.watermark-wrapper',
  ];

  const PIN_ICON_CACHE = new Map();
  const CUSTOM_PIN_SIZE = 48;

  const getPinIconUrl = (variant, icons) => {
    const url = icons[variant];
    if (!url) return null;

    if (PIN_ICON_CACHE.has(url)) return PIN_ICON_CACHE.get(url);

    PIN_ICON_CACHE.set(url, url);
    return url;
  };

  const getStoreTagSet = (store) =>
    (store?.tags || '')
      .split(',')
      .map((tag) => tag.trim().toLowerCase())
      .filter(Boolean);

  const getStorePinVariant = (store, tags) => {
    const tagSet = getStoreTagSet(store);
    if (tagSet.includes(tags.served.toLowerCase())) return 'served';
    if (tagSet.includes(tags.sold.toLowerCase())) return 'sold';
    return 'default';
  };

  const patchStoreMarkerData = (bss, tags, icons) => {
    if (!bss) return;

    const patchStore = (store) => {
      if (!store) return;

      const variant = getStorePinVariant(store, tags);
      if (variant === 'default') {
        if (store._storeLocatorOriginalMarker) {
          store.marker = store._storeLocatorOriginalMarker;
        }
        return;
      }

      if (!store._storeLocatorOriginalMarker) {
        store._storeLocatorOriginalMarker = store.marker;
      }

      const iconUrl = getPinIconUrl(variant, icons);
      if (iconUrl) store.marker = iconUrl;
    };

    (bss.configData || []).forEach(patchStore);
    (bss.searchResult || []).forEach(patchStore);

    if (bss.iconArr?.length && bss.locationArr?.length) {
      bss.locationArr.forEach((location, index) => {
        const store = getStoreById(bss, location.id);
        if (!store || !bss.iconArr[index]) return;

        const variant = getStorePinVariant(store, tags);
        if (variant === 'default') {
          if (store._storeLocatorOriginalMarker) bss.iconArr[index] = store._storeLocatorOriginalMarker;
          return;
        }

        const iconUrl = getPinIconUrl(variant, icons);
        if (iconUrl) bss.iconArr[index] = iconUrl;
      });
    }
  };

  const getMarkerImage = (marker) => {
    if (!marker) return null;

    const content = marker.content;
    if (content instanceof HTMLImageElement) return content;
    if (content?.querySelector) {
      const img = content.querySelector('img');
      if (img) return img;
    }

    const element = typeof marker.getElement === 'function' ? marker.getElement() : null;
    if (element instanceof HTMLImageElement) return element;
    if (element?.querySelector) {
      const img = element.querySelector('img');
      if (img) return img;
    }

    const icon = marker._icon;
    if (icon instanceof HTMLImageElement) return icon;
    if (icon?.querySelector) {
      const img = icon.querySelector('img');
      if (img) return img;
    }

    return null;
  };

  const getStoreById = (bss, storeId) => {
    if (!storeId) return null;
    const key = String(storeId);

    if (bss.configMap?.get) {
      return bss.configMap.get(key) || bss.configMap.get(Number(key)) || null;
    }

    return (bss.configData || []).find((store) => String(store.id) === key) || null;
  };

  const getStoreForMarker = (bss, marker) => {
    if (marker?.config) return marker.config;

    const markerId = marker?.id || marker?._storeId;
    if (markerId) {
      const parts = String(markerId).split(',');
      const store = getStoreById(bss, parts[parts.length - 1]);
      if (store) return store;
    }

    const position = marker?.position || marker?.getLatLng?.();
    if (!position) return null;

    const lat = typeof position.lat === 'function' ? position.lat() : position.lat;
    const lng = typeof position.lng === 'function' ? position.lng() : position.lng;
    if (!Number.isFinite(+lat) || !Number.isFinite(+lng)) return null;

    const stores = bss.searchResult?.length ? bss.searchResult : bss.configData || [];
    return (
      stores.find((store) => Math.abs(+store.lat - +lat) < 0.00001 && Math.abs(+store.lng - +lng) < 0.00001) ||
      null
    );
  };

  const collectMapMarkers = (bss) => {
    const markers = new Set();

    (bss.markersArr || []).forEach((marker) => {
      if (marker) markers.add(marker);
    });

    (bss.markerCluster?.markers || []).forEach((marker) => {
      if (marker) markers.add(marker);
    });

    if (bss.markerGroupMap?.values) {
      bss.markerGroupMap.forEach((marker) => {
        if (marker) markers.add(marker);
      });
    }

    return Array.from(markers);
  };

  roots.forEach((root) => {
    const widget = root.querySelector('[data-store-mappy-widget]');
    const listPanel = root.querySelector('[data-store-list-panel]');
    const searchForm = root.querySelector('[data-store-search-form]');
    const searchInput = root.querySelector('[data-store-search-input]');
    const searchButton = root.querySelector('.store-locator__search-button');
    const statusEl = root.querySelector('[data-store-search-status]');
    const filterButtons = root.querySelectorAll('[data-store-filter]');

    if (!widget || !listPanel) return;

    let mappyReady = false;
    let searchReady = false;
    let activeFilter = 'all';

    const setStatus = (message) => {
      if (!statusEl) return;
      statusEl.textContent = message;
    };

    const setActiveFilter = (name) => {
      filterButtons.forEach((button) => {
        const isActive = button.dataset.storeFilter === name;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    };

    const queryInWidget = (selector) => widget.querySelector(selector);

    const getMappyInput = () => document.getElementById('bss-search-input') || queryInWidget(SELECTORS.searchInput);

    const getMappyButton = () => document.getElementById('bss-search-btn');

    const getFilterTags = () => ({
      served: root.dataset.filterServedTag?.trim() || 'served',
      sold: root.dataset.filterSoldTag?.trim() || 'sold',
    });

    const getPinIcons = () => ({
      served: root.dataset.pinServedUrl?.trim() || '',
      sold: root.dataset.pinSoldUrl?.trim() || '',
    });

    const restoreDefaultPin = (image, store) => {
      const original = store?.marker || image.dataset.storeLocatorOriginalSrc;
      if (!original) return;

      image.src = original;
      image.style.width = '';
      image.style.height = '';
      image.style.maxWidth = '';
      image.style.maxHeight = '';
      delete image.dataset.storeLocatorPin;
      delete image.dataset.storeLocatorOriginalSrc;
    };

    let pinColorsTimer = null;
    let pinsReady = false;
    let pinsReadyFallbackTimer = null;

    const markPinsReady = () => {
      if (pinsReady) return;
      pinsReady = true;
      root.classList.add('is-pins-ready');
      window.clearTimeout(pinsReadyFallbackTimer);
    };

    const applyPinColors = (immediate = false) => {
      const bss = window.BSS_SL;
      if (!bss) return false;

      const tags = getFilterTags();
      const icons = getPinIcons();
      patchStoreMarkerData(bss, tags, icons);

      const markers = collectMapMarkers(bss);
      if (!markers.length) return false;

      markers.forEach((marker) => {
        const store = getStoreForMarker(bss, marker);
        if (!store) return;

        const variant = getStorePinVariant(store, tags);
        const image = getMarkerImage(marker);
        if (!image) return;

        if (variant === 'default') {
          if (image.dataset.storeLocatorPin) restoreDefaultPin(image, store);
          return;
        }

        if (!image.dataset.storeLocatorOriginalSrc) {
          image.dataset.storeLocatorOriginalSrc = store._storeLocatorOriginalMarker || store.marker || image.src;
        }

        const iconUrl = getPinIconUrl(variant, icons);
        if (!iconUrl) return;
        if (image.src === iconUrl && image.dataset.storeLocatorPin === variant) return;

        image.src = iconUrl;
        image.dataset.storeLocatorPin = variant;
        image.style.width = `${CUSTOM_PIN_SIZE}px`;
        image.style.height = 'auto';
        image.style.maxWidth = `${CUSTOM_PIN_SIZE}px`;
        image.style.objectFit = 'contain';
        image.alt = `bss-marker-${variant}`;
      });

      markPinsReady();
      return true;
    };

    const schedulePinColors = (immediate = false) => {
      if (immediate) {
        window.clearTimeout(pinColorsTimer);
        applyPinColors(true);
        return;
      }

      window.clearTimeout(pinColorsTimer);
      pinColorsTimer = window.setTimeout(() => applyPinColors(false), 16);
    };

    pinsReadyFallbackTimer = window.setTimeout(() => markPinsReady(), 4000);

    let lastConfigCount = 0;
    const watchBssStores = () => {
      const bss = window.BSS_SL;
      if (!bss) return;

      const count = bss.configData?.length || 0;
      if (count && count !== lastConfigCount) {
        lastConfigCount = count;
        schedulePinColors(true);
      } else {
        patchStoreMarkerData(bss, getFilterTags(), getPinIcons());
      }
    };

    const bssWatchPoll = window.setInterval(watchBssStores, 16);
    window.setTimeout(() => window.clearInterval(bssWatchPoll), 30000);

    const findTagInput = (tagName) => {
      const normalized = tagName.trim().toLowerCase();
      const containers = [
        document.getElementById('bss-filter-tag'),
        document.getElementById('dropdown-tag'),
        queryInWidget('#bss-filter-tag'),
        queryInWidget('#dropdown-tag'),
      ].filter(Boolean);

      for (const container of containers) {
        const inputs = container.querySelectorAll('input.tag, input[type="checkbox"]');
        for (const input of inputs) {
          if ((input.value || '').trim().toLowerCase() === normalized) {
            return input;
          }
        }
      }

      return null;
    };

    const clearMappyFilters = () => {
      if (typeof window.handleClearFilter === 'function') {
        window.handleClearFilter();
      }
    };

    const selectMappyTag = (tagName) => {
      const input = findTagInput(tagName);
      if (!input) return false;

      if (!input.checked) {
        input.checked = true;
        if (typeof window.handleChange === 'function') {
          window.handleChange(input);
        } else {
          input.click();
        }
      }

      return true;
    };

    const updateFilterStatus = () => {
      if (!statusEl || searchInput?.value.trim() || activeFilter === 'all') return;

      const count = window.BSS_SL?.searchResult?.length ?? 0;
      const tags = getFilterTags();
      const tag = activeFilter === 'served' ? tags.served : tags.sold;

      if (count === 0) {
        setStatus(`No ${activeFilter} locations found. Add the "${tag}" tag to stores in TA MAPPY.`);
      } else {
        setStatus(`${count} ${activeFilter} location${count === 1 ? '' : 's'} shown`);
      }
    };

    const applyFilter = (filterName, attempt = 0) => {
      activeFilter = filterName || 'all';
      setActiveFilter(activeFilter);

      if (!mappyReady && attempt < 40) {
        window.setTimeout(() => applyFilter(filterName, attempt + 1), 250);
        return;
      }

      if (activeFilter === 'all') {
        clearMappyFilters();
        if (!searchInput?.value.trim()) setStatus('');
        schedulePinColors(true);
        return;
      }

      const tags = getFilterTags();
      const tag = activeFilter === 'served' ? tags.served : tags.sold;

      clearMappyFilters();

      window.requestAnimationFrame(() => {
        const applied = selectMappyTag(tag);

        if (!applied) {
          if (attempt < 40) {
            window.setTimeout(() => applyFilter(filterName, attempt + 1), 250);
            return;
          }

          setStatus(`Tag "${tag}" was not found. Create it in TA MAPPY → Settings → Tags, then assign it to locations.`);
          return;
        }

        window.requestAnimationFrame(updateFilterStatus);
        schedulePinColors(true);
      });
    };

    const hideMappyChrome = () => {
      HIDE_IN_WIDGET.forEach((selector) => {
        widget.querySelectorAll(selector).forEach((node) => {
          node.setAttribute('hidden', 'hidden');
          node.setAttribute('aria-hidden', 'true');
        });
      });
    };

    const moveStoreList = () => {
      const list = document.getElementById('bss-search-result') || queryInWidget(SELECTORS.searchResult);
      if (!list || list.dataset.movedToPanel === 'true') return false;

      listPanel.appendChild(list);
      list.dataset.movedToPanel = 'true';
      list.classList.add('store-locator__mappy-list');
      return true;
    };

    const normalizeMapLayout = () => {
      const pageParent = queryInWidget(SELECTORS.pageParent);
      const page = queryInWidget(SELECTORS.page);
      const mapWrapper = queryInWidget(SELECTORS.mapWrapper);

      [pageParent, page, mapWrapper].forEach((node) => {
        if (!node) return;
        node.style.width = '100%';
        node.style.maxWidth = '100%';
        node.style.margin = '0';
        node.style.padding = '0';
        node.style.maxHeight = '805px';
        node.style.overflow = 'hidden';
      });

      if (page) {
        page.style.display = 'block';
      }

      if (mapWrapper) {
        mapWrapper.style.height = '805px';
        mapWrapper.style.minHeight = '805px';
        mapWrapper.style.maxHeight = '805px';
      }
    };

    const markReady = () => {
      if (mappyReady) return;
      mappyReady = true;
      root.classList.add('is-mappy-ready');
    };

    let mapsErrorReported = false;

    const detectGoogleMapsError = () => {
      if (mapsErrorReported) return;

      const errorNode =
        widget.querySelector('.gm-err-container') ||
        document.querySelector('.gm-err-container');

      if (!errorNode) return;

      const errorText = (errorNode.textContent || '').trim();
      if (!/can.?t load google maps/i.test(errorText)) return;

      mapsErrorReported = true;

      if (window.Shopify?.designMode) {
        setStatus(
          'Google Maps failed to load. In Shopify Admin go to Apps → TA MAPPY → Settings → Google API and add a valid key (billing + Maps JavaScript API enabled). For local preview, also allow http://127.0.0.1:9292/* in Google Cloud referrer restrictions — or switch TA MAPPY to OpenStreetMap (no API key).'
        );
      }
    };

    const integrateMappy = () => {
      const page = queryInWidget(SELECTORS.page);
      const mapWrapper = queryInWidget(SELECTORS.mapWrapper);
      if (!page || !mapWrapper) return false;

      hideMappyChrome();
      moveStoreList();
      normalizeMapLayout();
      detectGoogleMapsError();
      markReady();
      schedulePinColors(true);
      return true;
    };

    const isMappySearchReady = () => {
      const bss = window.BSS_SL;
      return Boolean(bss?.configData?.length && getMappyInput() && getMappyButton());
    };

    const restoreMapLayouts = (bss, desktopLayout, mobileLayout) => {
      if (!bss?.mapLayout) return;
      if (desktopLayout && bss.mapLayout.desktop) bss.mapLayout.desktop.layout = desktopLayout;
      if (mobileLayout && bss.mapLayout.mobile) bss.mapLayout.mobile.layout = mobileLayout;
    };

    const triggerMappySearch = (value) => {
      const bss = window.BSS_SL;
      const mappyInput = getMappyInput();
      const mappyButton = getMappyButton();

      if (!bss?.configData || !mappyInput || !mappyButton) {
        setStatus('Store locator is still loading. Please try again in a moment.');
        return false;
      }

      const query = value.trim();
      searchInput?.setAttribute('aria-busy', 'true');
      searchButton?.setAttribute('disabled', 'disabled');
      setStatus(query ? 'Searching locations…' : '');

      mappyInput.value = query;
      bss.searchInput = query;
      bss.searchSelected = 'Location';

      const desktopLayout = bss.mapLayout?.desktop?.layout;
      const mobileLayout = bss.mapLayout?.mobile?.layout;

      if (bss.mapLayout?.desktop) bss.mapLayout.desktop.layout = 'list';
      if (bss.mapLayout?.mobile) bss.mapLayout.mobile.layout = 'list';

      mappyButton.click();

      window.requestAnimationFrame(() => {
        restoreMapLayouts(bss, desktopLayout, mobileLayout);

        const resultCount = bss.searchResult?.length ?? 0;
        if (!query) {
          setStatus('');
        } else if (resultCount === 0) {
          setStatus('No locations found. Try a city, postcode, or store name.');
        } else {
          setStatus(`${resultCount} location${resultCount === 1 ? '' : 's'} found`);
        }

        searchInput?.removeAttribute('aria-busy');
        searchButton?.removeAttribute('disabled');
        schedulePinColors(true);
      });

      return true;
    };

    const resetSearch = () => {
      if (searchInput) searchInput.value = '';
      setStatus('');
      applyFilter('all');
    };

    filterButtons.forEach((button) => {
      button.addEventListener('click', () => {
        if (searchInput?.value.trim()) {
          searchInput.value = '';
          setStatus('');
        }
        applyFilter(button.dataset.storeFilter || 'all');
      });
    });

    if (searchForm && searchInput) {
      searchForm.addEventListener('submit', (event) => {
        event.preventDefault();

        if (!searchReady) {
          setStatus('Store locator is still loading. Please try again in a moment.');
          return;
        }

        const query = searchInput.value.trim();
        if (!query) {
          resetSearch();
          return;
        }

        triggerMappySearch(query);
      });
    }

    const observer = new MutationObserver(() => {
      integrateMappy();
      schedulePinColors(true);
      if (!searchReady && isMappySearchReady()) {
        searchReady = true;
        setStatus('');
      }
    });

    observer.observe(widget, { childList: true, subtree: true });

    const mapColumn = root.querySelector('.store-locator__map');
    if (mapColumn) {
      const mapObserver = new MutationObserver(() => schedulePinColors(true));
      mapObserver.observe(mapColumn, { childList: true, subtree: true });
    }

    const poll = window.setInterval(() => {
      if (integrateMappy()) {
        window.clearInterval(poll);
        applyFilter('all');
      }

      if (!searchReady && isMappySearchReady()) {
        searchReady = true;
        setStatus('');
      }
    }, 250);

    window.setTimeout(() => window.clearInterval(poll), 20000);

    integrateMappy();
    applyFilter('all');
  });
})();
